import React, { useCallback, useState, useEffect, useRef, Suspense, useMemo } from "react";
import type { ReactElement, ComponentProps, Dispatch, SetStateAction, RefObject } from "react";

import { DatePickerModal } from "@pianofunclub/react-native-paper-dates";
import type { NavigationAction } from "@react-navigation/native";
import { format } from "date-fns";
import * as Clipboard from "expo-clipboard";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Progress,
  Center,
  ChevronLeftIcon,
  ChevronRightIcon,
  useTheme,
  Tooltip,
  useDisclose,
  PresenceTransition,
  Spinner,
  useToast,
  Checkbox,
  AddIcon,
} from "native-base";
import { Platform, useWindowDimensions } from "react-native";
import {
  usePreloadedQuery,
  usePaginationFragment,
  useRelayEnvironment,
  useRefetchableFragment,
  useMutation,
} from "react-relay";
import type { PreloadedQuery, UseQueryLoaderLoadQueryOptions } from "react-relay";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { fetchQuery } from "relay-runtime";
import type { Subscription } from "relay-runtime";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import Row from "pianofunclub-shared/components/Base/Row";
import type { TableData } from "pianofunclub-shared/components/Base/Row";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import DayPickerModal from "pianofunclub-shared/components/Modals/DayPickerModal";
import ManageRegisterLessonModal from "pianofunclub-shared/components/Modals/ManageRegisterLessonModal";
import type { RegisterUpdate } from "pianofunclub-shared/components/Modals/ManageRegisterLessonModal";
import SendRegisterChangeNotificationModal from "pianofunclub-shared/components/Modals/SendRegisterChangeNotificationModal";
import type { ShowUpdateBlockDetailsModalHandler } from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import CustomFlatListSpinner from "pianofunclub-shared/components/Other/CustomFlatListSpinner";
import {
  AccountsIcon,
  AddCircleIcon,
  CalendarIcon,
  CopyIcon,
  EditIcon,
  OptionsIcon,
  RegisterIcon,
  RestartIcon,
  SendIcon,
  SwapIconCircle,
  TimetableIcon,
  TrashIcon,
  UserClockIcon,
  UserSlashIcon,
} from "pianofunclub-shared/components/Other/Icons";
import SearchBar from "pianofunclub-shared/components/Other/SearchBar";
import LessonBlockTableRow from "pianofunclub-shared/components/Registers/LessonBlockTableRow";
import type { LessonBlockTableRowRef } from "pianofunclub-shared/components/Registers/LessonBlockTableRow";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { SchoolEdges } from "pianofunclub-shared/providers/DataProvider";
import type { LoadAccountQuery$data } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountQuery.graphql";
import type { RegisterInfoFragment_query$key } from "pianofunclub-shared/relay/graphql/fragments/__generated__/RegisterInfoFragment_query.graphql";
import type { RegisterInfoRefreshQuery } from "pianofunclub-shared/relay/graphql/fragments/__generated__/RegisterInfoRefreshQuery.graphql";
import { register_fragment } from "pianofunclub-shared/relay/graphql/fragments/RegisterInfoFragment";
import type {
  AddReplacementLessonColumnToRegisterMutation,
  AddReplacementLessonColumnToRegisterMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/AddReplacementLessonColumnToRegisterMutation.graphql";
import type {
  LoadLessonBlocks_query_lessonBlocks$key,
  LoadLessonBlocks_query_lessonBlocks$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonBlocks_query_lessonBlocks.graphql";
import type { LoadLessonBlocksPaginationQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonBlocksPaginationQuery.graphql";
import type {
  LoadLessonBlocksQuery,
  LoadLessonBlocksQuery$variables,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonBlocksQuery.graphql";
import type {
  RemoveLessonColumnMutation,
  RemoveLessonColumnMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/RemoveLessonColumnMutation.graphql";
import type {
  RescheduleLessonsForWeekMutation,
  RescheduleLessonsForWeekMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/RescheduleLessonsForWeekMutation.graphql";
import type {
  UpdateRegistersForWeekMutation,
  UpdateRegistersForWeekMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/UpdateRegistersForWeekMutation.graphql";
import { add_replacement_lesson_column_to_register } from "pianofunclub-shared/relay/graphql/registers/AddReplacementLessonColumnToRegister";
import {
  load_lesson_blocks,
  load_lesson_blocks_pagination,
} from "pianofunclub-shared/relay/graphql/registers/LoadLessonBlocks";
import { remove_lesson_column } from "pianofunclub-shared/relay/graphql/registers/RemoveLessonColumn";
import { reschedule_lessons_for_week } from "pianofunclub-shared/relay/graphql/registers/RescheduleLessonsForWeek";
import { update_registers_for_week } from "pianofunclub-shared/relay/graphql/registers/UpdateRegistersForWeek";
import { customColors } from "pianofunclub-shared/theme";
import { BLOCKS } from "pianofunclub-shared/utils/constants";
import {
  getLessonDayString,
  getScaledWindowDimension,
  teachingDaysToString,
  titleCaseConverter,
} from "pianofunclub-shared/utils/converters";
import {
  getDefaultStartingYearAndStartingYearsForSelect,
  getFullName,
} from "pianofunclub-shared/utils/extractors";
import { useDebounceFunction } from "pianofunclub-shared/utils/hooks";
import type { State, Action } from "pianofunclub-shared/utils/reducers";
import { teacherRegistersTableCsvConverter } from "pianofunclub-shared/utils/tableCsvConverters";

type VStackProps = ComponentProps<typeof VStack>;

export type LessonBlockData = NonNullable<
  LoadLessonBlocks_query_lessonBlocks$data["lessonBlocks"]
>["edges"][number];

export type LessonBlockDataNode = NonNullable<NonNullable<LessonBlockData>["node"]>;

export type RegisterProgress = {
  marked: number | undefined;
  total: number | undefined;
};

export type RegisterDates = (
  | {
      actualDate: Date | undefined;
      isReplacementLesson: boolean;
      replacementColumnIndex?: number | undefined;
      scheduledDate: Date | undefined;
    }
  | undefined
)[];

export type TeacherSchools = NonNullable<LoadAccountQuery$data["profile"]>["schools"];

export interface TeacherRegistersReducerValues {
  block: number;
  contentIsRendered: boolean;
  currentPageIndex?: number;
  dayIndex: number;
  isAddLessonBreakModal?: boolean;
  isRefetching: boolean;
  leaveAlertAction?: NavigationAction | (() => void);
  lessonBlocksConnectionId?: string;
  schoolId?: string;
  searchTerm: string;
  selectedSchoolIdOnModal?: string;
  selectSchoolModalIsOpen?: boolean;
  sendRegisterChangeNotificationModalIsOpen?: boolean;
  startingYear: number;
  subscription?: Subscription;
  teachingDays: readonly (number | null | undefined)[];
  updateLessonBlockTimeModalIsOpen?: boolean;
}

export interface TeacherRegistersProps extends VStackProps {
  allSchools: SchoolEdges;
  dispatchState: Dispatch<Action<TeacherRegistersReducerValues>>;
  lessonBlocksConnectionId?: string;
  loadLessonBlocksQuery: (
    variables: LoadLessonBlocksQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions
  ) => void;
  loadLessonBlocksQueryReference:
    | PreloadedQuery<LoadLessonBlocksQuery, Record<string, unknown>>
    | null
    | undefined;
  navigation: any;
  profileFirstName?: string;
  profileId: string;
  profileLastName?: string;
  registerProgressCounter: RefObject<RegisterProgress>;
  registerUpdates: RefObject<RegisterUpdate[]>;
  removeLessonBlocks?: (
    lessonBlockIds: string[],
    connectionId?: string,
    onComplete?: () => void
  ) => void;
  removeLessonBlocksInFlight?: boolean;
  showUpdateBlockDetailsModalHandler: ShowUpdateBlockDetailsModalHandler;
  sideBarWidth: number;
  startingYears: {
    label: string;
    value: number;
  }[];
  state: State<TeacherRegistersReducerValues>;
  teacherSchools?: TeacherSchools;
  updateLessonBlockDetails: (
    variables: {
      instrument?: string;
      keepFree?: boolean;
      lessonBlockIds: string[];
      pupilId?: string | null;
      staffNoteForTeacher?: string;
      stageId?: string;
      teacherId?: string | null;
      teacherNoteForStaff?: string;
    },
    lessonBlockCursor?: string,
    onComplete?: () => void
  ) => void;
  updateRegisterState: (
    variables: {
      lessonId: string;
      registerNote?: string;
      status: string;
    },
    progressChange?: number
  ) => void;
  updateScheduledLessonBlockTime?: (
    lessonBlockIds: string[],
    dayIndex: number,
    hours?: number,
    minutes?: number,
    onComplete?: () => void
  ) => void;
  updateScheduledLessonBlockTimeInFlight?: boolean;
  userIsTeacher?: boolean;
}

interface ContentProps extends TeacherRegistersProps {
  dataProvider: DataProvider;
  lessonBlockRefs: RefObject<Map<string, LessonBlockTableRowRef>>;
  loadLessonBlocksQueryReference: PreloadedQuery<LoadLessonBlocksQuery, Record<string, unknown>>;
  refreshHandler: () => void;
  selectedBlocks: string[];
  setCopyDataToClipboard: Dispatch<SetStateAction<(() => void | undefined) | undefined>>;
  setDataProvider: Dispatch<SetStateAction<DataProvider>>;
  setSelectedBlocks: Dispatch<SetStateAction<string[]>>;
}

export type LessonDataType =
  | {
      actualDate?: Date;
      isReplacementLesson: boolean;
      replacementColumnIndex?: number;
      scheduledDate?: Date | undefined;
    }
  | undefined;

const TABLE_BORDER_COLOR = "surface.400";
const TABLE_BORDER_WIDTH = 1;
const TABLE_BORDER_RADIUS = 10;
const LOAD_X_LESSON_BLOCKS = Platform.OS === "web" ? 15 : 5;

const TeacherRegistersContent = (props: ContentProps) => {
  const {
    dataProvider,
    dispatchState,
    lessonBlockRefs,
    loadLessonBlocksQueryReference,
    navigation,
    profileId,
    refreshHandler,
    registerProgressCounter,
    registerUpdates,
    selectedBlocks,
    setCopyDataToClipboard,
    setDataProvider,
    setSelectedBlocks,
    showUpdateBlockDetailsModalHandler,
    sideBarWidth,
    state,
    updateRegisterState,
    updateScheduledLessonBlockTime,
    userIsTeacher,
  } = props;

  const { user } = useAuth();

  const data = usePreloadedQuery(load_lesson_blocks, loadLessonBlocksQueryReference);

  const [registerInfo, refetchRegisterInfo] = useRefetchableFragment<
    RegisterInfoRefreshQuery,
    RegisterInfoFragment_query$key
  >(register_fragment, data);

  useEffect(() => {
    dispatchState({
      input: "teachingDays",
      value: registerInfo.profile?.teachingDays ?? [],
    });
    dispatchState({
      input: "dayIndex",
      value:
        registerInfo.profile?.teachingDays &&
        registerInfo.profile.teachingDays.length > 0 &&
        !registerInfo.profile.teachingDays.includes(state.values.dayIndex)
          ? (registerInfo.profile.teachingDays[0] ?? state.values.dayIndex)
          : state.values.dayIndex,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerInfo.profile?.teachingDays]);

  const [lessonBlockIds, setLessonBlockIds] = useState<string[]>([]);

  const {
    data: lessonBlocksData,
    hasNext,
    isLoadingNext,
    loadNext,
    refetch: refresh,
  } = usePaginationFragment<
    LoadLessonBlocksPaginationQuery,
    LoadLessonBlocks_query_lessonBlocks$key
  >(load_lesson_blocks_pagination, data);

  const datesToRender = useMemo(() => {
    const firstLessonBlock = lessonBlocksData?.lessonBlocks?.edges[0]?.node;

    return (firstLessonBlock?.lessons?.edges ?? []).map((item) => {
      if (!item?.node) {
        return undefined;
      }

      const isReplacementLesson = typeof item.node.replacementLessonColumnIndex === "number";

      let actualDate: Date | undefined = undefined;
      let scheduledDate: Date | undefined = undefined;

      // if the week has been rearranged, use the rearranged date in the header
      if (item.node.rearrangedTimestamp && item?.node.weekWasRearranged) {
        actualDate = new Date(item.node.rearrangedTimestamp);
        scheduledDate = item.node.scheduledTimestamp
          ? new Date(item.node.scheduledTimestamp)
          : undefined;
      } else if (item.node.scheduledTimestamp) {
        actualDate = new Date(item.node.scheduledTimestamp);
        scheduledDate = actualDate;
      }

      if (isReplacementLesson) {
        return {
          actualDate,
          isReplacementLesson,
          replacementColumnIndex: item.node.replacementLessonColumnIndex,
          scheduledDate,
        };
      } else if (actualDate) {
        // non-replacement lessons with a defined date
        return {
          actualDate,
          isReplacementLesson: false,
          scheduledDate,
        };
      }

      return undefined;
    });
  }, [lessonBlocksData?.lessonBlocks?.edges]);

  const currentStartingYear = useMemo(() => {
    return (
      user?.profile?.profileGroup?.currentStartingYear ??
      getDefaultStartingYearAndStartingYearsForSelect()[0]
    );
  }, [user?.profile?.profileGroup?.currentStartingYear]);

  const flexArray = useMemo(() => {
    const flexArray =
      Platform.OS === "web" ? [0.8, 1, 0.5, 0.4, 0.6, 1, 0.3] : [0.8, 1, 0.6, 1, 0.5];
    flexArray.push(...Array(datesToRender.length).fill(0.5));
    if (Platform.OS === "web" && !userIsTeacher) {
      flexArray.unshift(0.3);
    }
    return flexArray;
  }, [datesToRender.length, userIsTeacher]);

  useEffect(() => {
    dispatchState({
      input: "lessonBlocksConnectionId",
      value: lessonBlocksData.lessonBlocks?.__id,
    });
    if (lessonBlocksData.lessonBlocks?.edges && lessonBlocksData.lessonBlocks.edges.length > 0) {
      setDataProvider(
        dataProvider.cloneWithRows(
          // hide any lesson blocks that are not taught by the teacher
          // and are not the correct lesson day
          // this happens when changing the teacher/day of lesson block(s)
          lessonBlocksData.lessonBlocks.edges.filter(
            (item) =>
              item?.node?.teacher?.id === profileId &&
              item?.node?.lessonDay === state.values.dayIndex
          )
        )
      );
      setLessonBlockIds(lessonBlocksData.lessonBlocks.edges.map((item) => item?.node?.id ?? ""));
      // if there is no data, create a fresh DataProvider
    } else {
      setDataProvider(
        new DataProvider((r1, r2) => {
          return r1 !== r2;
        })
      );
      setLessonBlockIds([]);
    }

    setCopyDataToClipboard(() => () => {
      Clipboard.setStringAsync(
        teacherRegistersTableCsvConverter(
          lessonBlocksData.lessonBlocks?.edges ?? [],
          datesToRender ?? []
        )
      );
      toast.show({
        render: ({ id }: { id: string }) => (
          <ToastAlert id={id} status="success" title={"Copied Data to Clipboard"} toast={toast} />
        ),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lessonBlocksData.lessonBlocks?.__id,
    lessonBlocksData.lessonBlocks?.edges,
    loadLessonBlocksQueryReference,
  ]);

  useEffect(() => {
    if (registerInfo.profile?.totalLessonsMarked != null) {
      registerProgressCounter.current.marked = registerInfo.profile.totalLessonsMarked;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerInfo.profile?.totalLessonsMarked]);

  useEffect(() => {
    if (registerInfo.profile?.totalLessons != null) {
      registerProgressCounter.current.total = registerInfo.profile.totalLessons;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerInfo.profile?.totalLessons]);

  const [layoutProvider, setLayoutProvider] = useState(
    new LayoutProvider(
      () => "VSEL",
      (type, dim) => {
        switch (type) {
          case "VSEL":
            dim.width = getScaledWindowDimension(windowWidth, scale);
            dim.height = 40;
            break;
          default:
            dim.width = 0;
            dim.height = 0;
        }
      }
    )
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(LOAD_X_LESSON_BLOCKS);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const { colors } = useTheme();
  // @ts-expect-error ref for RLV not typed properly
  const recyclerListViewRef = useRef<RecyclerListView>();
  // stop refetch useEffects firing on first render
  const renderCount = useRef(0);
  const environment = useRelayEnvironment();

  const refetchLessonBlocks = useCallback(
    (
      variables: {
        block: number;
        dayIndex: number;
        schoolId?: string;
        searchTerm?: string;
        startingYear: number;
      },
      options?: { forceRefetch?: boolean; skipLessonBlocks?: boolean }
    ): Subscription | undefined => {
      if (!options?.skipLessonBlocks) {
        dispatchState({ input: "isRefetching", value: true });
      }

      const fullVariables = {
        ...variables,
        first: LOAD_X_LESSON_BLOCKS,
        profileId: profileId,
        skipLessonBlocks: options?.skipLessonBlocks ?? false,
        skipLessonStages: true,
        skipRegisterProgress: false,
      };

      const subscription = fetchQuery<LoadLessonBlocksQuery>(
        environment,
        load_lesson_blocks,
        fullVariables,
        {
          fetchPolicy: options?.forceRefetch ? "network-only" : "store-or-network",
        }
      ).subscribe({
        complete: () => {
          if (!options?.skipLessonBlocks) {
            setSelectedBlocks([]);
            lessonBlockRefs.current.forEach((ref) => {
              ref.setIsChecked(false);
            });
            lessonBlockRefs.current = new Map();
          }
          dispatchState({ input: "isRefetching", value: false });
          refetchRegisterInfo(fullVariables, {
            fetchPolicy: "store-only",
          });
          refresh(fullVariables, { fetchPolicy: "store-only" });
        },
        error: () => {
          dispatchState({ input: "isRefetching", value: false });
        },
      });
      return subscription;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchState, environment, profileId, refetchRegisterInfo, refresh]
  );

  useEffect(() => {
    if (renderCount.current < 4) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    dispatchState({
      input: "subscription",
      value: refetchLessonBlocks({
        block: state.values.block,
        dayIndex: state.values.dayIndex,
        schoolId: state.values.schoolId,
        searchTerm: state.values.searchTerm.trim(),
        startingYear: state.values.startingYear,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.startingYear, state.values.block, state.values.schoolId, state.values.dayIndex]);

  useDebounceFunction(
    () => {
      if (renderCount.current < 4) {
        renderCount.current += 1;
        return;
      }

      dispatchState({ input: "isRefetching", value: false });
      state.values.subscription?.unsubscribe();

      dispatchState({
        input: "subscription",
        value: refetchLessonBlocks({
          block: state.values.block,
          dayIndex: state.values.dayIndex,
          schoolId: state.values.schoolId,
          searchTerm: state.values.searchTerm.trim(),
          startingYear: state.values.startingYear,
        }),
      });
    }, // no delay if clearing text
    state.values.searchTerm !== "" ? 750 : 0,

    [state.values.searchTerm]
  );

  useEffect(() => {
    if (renderCount.current < 4) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    refreshHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values?.currentPageIndex]);

  const { scale, width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    if (renderCount.current < 4) {
      renderCount.current += 1;
      return;
    }
    // this allows the RLV to resize when the window resizes
    setLayoutProvider(
      new LayoutProvider(
        () => "VSEL",
        (type, dim) => {
          switch (type) {
            case "VSEL":
              dim.width = getScaledWindowDimension(windowWidth, scale) - sideBarWidth;
              dim.height = 40;
              break;
            default:
              dim.width = 0;
              dim.height = 0;
          }
        }
      )
    );
  }, [scale, windowWidth, sideBarWidth]);

  const toast = useToast();
  const {
    isOpen: columnOptionsIsOpen,
    onClose: columnOptionsOnClose,
    onOpen: columnOptionsOnOpen,
  } = useDisclose();
  const [selectedColumnState, setSelectedColumnState] = useState<LessonDataType>();
  const [datePickerState, setDatePickerState] = useState<{
    // this is the initial picker date - will match the date of the lesson
    // (rearranged or not)
    date: Date | undefined;
    isOpen: boolean;
    replacementLessonColumnIndex: number | undefined;
    // this is the orginal scheduled date of the lessons - used to find the lessons
    // when running mutations
    scheduledDate: Date | undefined;
  }>({
    date: undefined,
    isOpen: false,
    replacementLessonColumnIndex: undefined,
    scheduledDate: undefined,
  });
  const [addReplacementColumnDatePickerState, setAddReplacementColumnDatePickerState] = useState<{
    date?: Date;
    isOpen: boolean;
  }>({
    date: undefined,
    isOpen: false,
  });
  const [registerModalIsOpen, setRegisterModalIsOpen] = useState(false);

  const [commitRescheduleLessonsForWeek, rescheduleLessonsForWeekInFlight] =
    useMutation<RescheduleLessonsForWeekMutation>(reschedule_lessons_for_week);

  const rescheduleLessonsForWeek = useCallback(
    (variables: {
      rearrangedDate: Date;
      replacementLessonColumnIndex?: number;
      scheduledDate: Date;
      schoolId?: string;
    }) => {
      const rescheduleLessonsForWeekConfig = {
        variables: {
          input: {
            block: state.values.block,
            dayIndex: state.values.dayIndex,
            rearrangedDate: format(variables.rearrangedDate, "yyyy-MM-dd"),
            replacementLessonColumnIndex: variables.replacementLessonColumnIndex,
            scheduledDate: format(variables.scheduledDate, "yyyy-MM-dd"),
            schoolId: variables.schoolId,
            startingYear: state.values.startingYear,
            teacherId: profileId,
          },
        },
        onCompleted: (response: RescheduleLessonsForWeekMutation$data) => {
          if (response?.rescheduleLessonsForWeek?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Rescheduled lessons for week"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't reschedule lessons"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitRescheduleLessonsForWeek(rescheduleLessonsForWeekConfig);
    },
    [
      commitRescheduleLessonsForWeek,
      profileId,
      state.values.block,
      state.values.dayIndex,
      state.values.startingYear,
      toast,
    ]
  );

  const [commitUpdateRegistersForWeek, updateRegistersForWeekInFlight] =
    useMutation<UpdateRegistersForWeekMutation>(update_registers_for_week);

  const updateRegistersForWeek = useCallback(
    (variables: { replacementLessonIndex?: number; scheduledDate?: Date; status: string }) => {
      const updateRegistersForWeekConfig = {
        variables: {
          input: {
            block: state.values.block,
            dayIndex: state.values.dayIndex,
            replacementLessonIndex: variables.replacementLessonIndex,
            scheduledDate: variables.scheduledDate
              ? format(variables.scheduledDate, "yyyy-MM-dd")
              : undefined,
            schoolId: state.values.schoolId,
            startingYear: state.values.startingYear,
            status: variables.status,
            teacherId: profileId,
          },
        },
        onCompleted: (response: UpdateRegistersForWeekMutation$data) => {
          refetchLessonBlocks(
            {
              block: state.values.block,
              dayIndex: state.values.dayIndex,
              schoolId: state.values.schoolId,
              searchTerm: state.values.searchTerm.trim(),
              startingYear: state.values.startingYear,
            },
            { forceRefetch: true, skipLessonBlocks: true }
          );
          if (response?.updateRegistersForWeek?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Updated registers for week"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update registers"}
                  toast={toast}
                />
              ),
            });
          }
          setSelectedColumnState(undefined);
        },
      };

      commitUpdateRegistersForWeek(updateRegistersForWeekConfig);
    },
    [
      commitUpdateRegistersForWeek,
      profileId,
      refetchLessonBlocks,
      state.values.block,
      state.values.dayIndex,
      state.values.schoolId,
      state.values.searchTerm,
      state.values.startingYear,
      toast,
    ]
  );

  const [commitAddReplacementLessonColumnToRegister, addReplacementLessonColumnToRegisterInFlight] =
    useMutation<AddReplacementLessonColumnToRegisterMutation>(
      add_replacement_lesson_column_to_register
    );

  const addReplacementLessonColumnToRegister = useCallback(
    (variables: { scheduledDate: Date }) => {
      const addReplacementLessonColumnToRegisterConfig = {
        variables: {
          input: {
            block: state.values.block,
            dayIndex: state.values.dayIndex,
            scheduledDate: format(variables.scheduledDate, "yyyy-MM-dd"),
            startingYear: state.values.startingYear,
            teacherId: profileId,
          },
        },
        onCompleted: (response: AddReplacementLessonColumnToRegisterMutation$data) => {
          if (response?.addReplacementLessonColumnToRegister?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Added replacement lesson column"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't add replacement lesson column"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitAddReplacementLessonColumnToRegister(addReplacementLessonColumnToRegisterConfig);
    },
    [
      commitAddReplacementLessonColumnToRegister,
      profileId,
      state.values.block,
      state.values.dayIndex,
      state.values.startingYear,
      toast,
    ]
  );

  const [commitRemoveLessonColumn, removeLessonColumnInFlight] =
    useMutation<RemoveLessonColumnMutation>(remove_lesson_column);

  const removeLessonColumn = useCallback(
    (variables: { replacementLessonColumnIndex?: number; scheduledDate?: string }) => {
      const removeLessonColumnConfig = {
        variables: {
          input: {
            block: Number(state.values.block),
            dayIndex: state.values.dayIndex,
            replacementLessonColumnIndex: variables.replacementLessonColumnIndex,
            scheduledDate: variables.scheduledDate,
            startingYear: Number(state.values.startingYear),
            teacherId: profileId,
          },
        },
        onCompleted: (response: RemoveLessonColumnMutation$data) => {
          columnOptionsOnClose();
          setTimeout(() => {
            setSelectedColumnState(undefined);
          }, 500);
          if (response?.removeLessonColumn?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="error" title={"Removed lesson column"} toast={toast} />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't remove lesson column"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitRemoveLessonColumn(removeLessonColumnConfig);
    },
    [
      columnOptionsOnClose,
      commitRemoveLessonColumn,
      profileId,
      state.values.block,
      state.values.dayIndex,
      state.values.startingYear,
      toast,
    ]
  );

  const updateRegistersForWeekHandler = useCallback(
    (variables: { status: string }) => {
      setRegisterModalIsOpen(false);
      if (!selectedColumnState) {
        return;
      }

      updateRegistersForWeek({
        ...(selectedColumnState.isReplacementLesson
          ? {
              replacementLessonIndex: selectedColumnState.replacementColumnIndex,
            }
          : {
              scheduledDate: selectedColumnState.scheduledDate,
            }),
        ...variables,
      });
    },
    [selectedColumnState, updateRegistersForWeek]
  );

  const onPressProfile = useCallback(
    (profileId: string | null | undefined) => {
      if (profileId) {
        navigation.push("Account", {
          accountType: "PUPIL",
          block: state.values.block,
          profileId: profileId,
          startingYear: state.values.startingYear,
        });
      }
    },
    [navigation, state.values.block, state.values.startingYear]
  );

  const onPressEnrolment = useCallback(
    (enrolmentId: string | null | undefined) => {
      if (enrolmentId) {
        navigation.push("Enrolment", {
          enrolmentId,
        });
      }
    },
    [navigation]
  );

  const onPressSchool = useCallback(
    (schoolId: string | null | undefined) => {
      if (schoolId) {
        navigation.push("School", {
          block: state.values.block,
          schoolId: schoolId,
          startingYear: state.values.startingYear,
        });
      }
    },
    [navigation, state.values.block, state.values.startingYear]
  );

  const checkboxChangeHandler = useCallback(
    (blockId: string, isSelected: boolean) => {
      if (isSelected) {
        setSelectedBlocks((blocks) => blocks.concat(blockId));
      } else {
        setSelectedBlocks((blocks) => blocks.filter((id) => id !== blockId));
      }
    },
    [setSelectedBlocks]
  );

  const selectAllCheckboxChangeHandler = useCallback(
    (isSelected: boolean) => {
      lessonBlockRefs.current.forEach((ref) => {
        ref.setIsChecked(isSelected);
      });
      setSelectedBlocks(isSelected ? lessonBlockIds : []);
    },
    [lessonBlockIds, lessonBlockRefs, setSelectedBlocks]
  );

  const rowRenderer = useCallback(
    (_: unknown, data: LessonBlockData, index: number): ReactElement | null => {
      if (!data?.node) {
        return null;
      }

      return (
        <Box borderColor={TABLE_BORDER_COLOR} borderLeftWidth={TABLE_BORDER_WIDTH}>
          <LessonBlockTableRow
            ref={(ref) => {
              if (ref && data.node?.id && !lessonBlockRefs.current.get(data.node.id)) {
                lessonBlockRefs.current.set(data.node.id, ref);
              }
            }}
            cellProps={{
              hideTopBorder: index === 0,
              placeholderTextProps: { color: "muted.400" },
              pressableTextProps: {
                fontFamily: "Poppins-Regular",
              },
              px: 2,
              textProps: {
                fontFamily: "Poppins-Light",
                fontSize: Platform.OS === "web" && windowWidth > 1000 ? "md" : "sm",
                isTruncated: true,
                textAlign: "center",
              },
            }}
            checkboxChangeHandler={checkboxChangeHandler}
            currentStartingYear={currentStartingYear}
            cursor={data.cursor}
            data={data.node}
            datesToRender={datesToRender}
            flexArray={flexArray}
            isRegisterTable
            onPressEnrolment={onPressEnrolment}
            onPressProfile={onPressProfile}
            onPressSchool={onPressSchool}
            registerUpdates={registerUpdates}
            rowHeight={10}
            selectedStartingYear={state.values.startingYear}
            showUpdateBlockDetailsModalHandler={showUpdateBlockDetailsModalHandler}
            tableBorderColor={TABLE_BORDER_COLOR}
            tableBorderWidth={TABLE_BORDER_WIDTH}
            updateRegisterState={updateRegisterState}
            updateScheduledLessonBlockTime={updateScheduledLessonBlockTime}
            userIsTeacher={userIsTeacher}
          />
        </Box>
      );
    },
    [
      checkboxChangeHandler,
      currentStartingYear,
      datesToRender,
      flexArray,
      lessonBlockRefs,
      onPressEnrolment,
      onPressProfile,
      onPressSchool,
      registerUpdates,
      showUpdateBlockDetailsModalHandler,
      state.values.startingYear,
      updateRegisterState,
      updateScheduledLessonBlockTime,
      userIsTeacher,
      windowWidth,
    ]
  );

  const renderFooter = useMemo(() => {
    const renderFooter = () =>
      isLoadingNext ? (
        <CustomFlatListSpinner
          borderColor={TABLE_BORDER_COLOR}
          borderTopWidth={1}
          position="relative"
          pt="8"
          top="0"
        />
      ) : null;
    return renderFooter;
  }, [isLoadingNext]);

  const renderListEmptyBanner = useMemo(() => {
    return (
      <ListEmptyBanner
        explainer={
          state.values.searchTerm || state.values.schoolId
            ? "No registers found..."
            : state.values.teachingDays.length > 0
              ? "No lessons on this day"
              : "No lessons in this block"
        }
        flexGrow={1}
      >
        <Text fontSize="6xl">{"😐"}</Text>
      </ListEmptyBanner>
    );
  }, [state.values.schoolId, state.values.searchTerm, state.values.teachingDays?.length]);

  const renderRefetchIndicator = useMemo(() => {
    return state.values.isRefetching ? <CustomFlatListSpinner top="60" /> : null;
  }, [state.values.isRefetching]);

  const renderTableHeader = useMemo(() => {
    const tableHeaders: TableData =
      Platform.OS === "web"
        ? [
            {
              data: "Time",
            },
            {
              data: "School",
            },
            {
              data: "Type",
            },
            {
              data: "Stage",
            },
            {
              data: "Instrument",
            },
          ]
        : [
            {
              data: "Time",
            },
            {
              data: "School",
            },
            {
              data: "Instrument",
            },
          ];
    tableHeaders.push(
      {
        data: "Pupil",
      },
      {
        data: "Year",
      },
      ...datesToRender.map((item) => {
        const isCurrentlySelectedColumn =
          (item?.isReplacementLesson &&
            item?.replacementColumnIndex === selectedColumnState?.replacementColumnIndex) ||
          (!item?.isReplacementLesson &&
            item?.scheduledDate === selectedColumnState?.scheduledDate);

        return {
          data:
            isCurrentlySelectedColumn &&
            (rescheduleLessonsForWeekInFlight || updateRegistersForWeekInFlight) ? (
              <Spinner />
            ) : item?.actualDate ? (
              item?.isReplacementLesson === true ? (
                <>
                  <HStack space="1.5">
                    <SwapIconCircle color="surface.900" size="5" />
                    <Text fontSize={Platform.OS === "web" ? "md" : "sm"}>
                      {format(item.actualDate, "dd/MM")}
                    </Text>
                  </HStack>
                </>
              ) : (
                format(item.actualDate, "dd/MM")
              )
            ) : (
              <>
                <HStack space="1.5">
                  <SwapIconCircle color="surface.900" size="5" />
                  <Text fontSize={Platform.OS === "web" ? "md" : "sm"}>...</Text>
                </HStack>
              </>
            ),
          onPress: () => {
            setSelectedColumnState(item);
            columnOptionsOnOpen();
          },
        };
      })
    );
    if (Platform.OS === "web" && !userIsTeacher) {
      tableHeaders.unshift({
        data: (
          <Checkbox
            isChecked={
              selectedBlocks.length != 0 &&
              lessonBlockIds.length <= selectedBlocks.length &&
              lessonBlockIds.every((id) => selectedBlocks.includes(id))
            }
            onChange={selectAllCheckboxChangeHandler}
            size="md"
            value="SELECT_ALL"
          />
        ),
        onPress: () => {
          return;
        },
      });
    }
    return (
      <Box bg="surface.100">
        <Box
          bg="primary.50"
          borderColor={TABLE_BORDER_COLOR}
          borderTopRadius={TABLE_BORDER_RADIUS}
          borderWidth={TABLE_BORDER_WIDTH}
        >
          <Row
            cellProps={{
              hideOuterSideBorder: true,
              hideTopBorder: true,
              px: 2,
              textProps: {
                fontSize: Platform.OS === "web" ? "md" : "sm",
                textAlign: "center",
              },
            }}
            data={tableHeaders}
            flexArray={flexArray}
            rowHeight={10}
            rowIndex={0}
            tableBorderColor={TABLE_BORDER_COLOR}
            tableBorderWidth={TABLE_BORDER_WIDTH}
          />
        </Box>
        {!userIsTeacher ? (
          <ButtonDebounced
            _hover={{ bg: "secondary.600", opacity: 1 }}
            _pressed={{ bg: "secondary.700", opacity: 1 }}
            borderRadius="full"
            colorScheme="secondary"
            leftIcon={
              <Center size="5">
                {addReplacementLessonColumnToRegisterInFlight ? (
                  <Spinner color="white" size="sm" />
                ) : (
                  <AddIcon color="white" size="4" />
                )}
              </Center>
            }
            onPress={() => {
              setAddReplacementColumnDatePickerState({
                date: new Date(),
                isOpen: true,
              });
            }}
            position="absolute"
            right="-12"
            shadow="2"
            size="7"
            top="-12"
          />
        ) : null}
      </Box>
    );
  }, [
    datesToRender,
    userIsTeacher,
    flexArray,
    addReplacementLessonColumnToRegisterInFlight,
    selectedColumnState?.replacementColumnIndex,
    selectedColumnState?.scheduledDate,
    rescheduleLessonsForWeekInFlight,
    updateRegistersForWeekInFlight,
    columnOptionsOnOpen,
    selectedBlocks,
    lessonBlockIds,
    selectAllCheckboxChangeHandler,
  ]);

  const renderColumnActionsheet = useMemo(() => {
    const getTitle = () => {
      const { actualDate, isReplacementLesson, replacementColumnIndex } = selectedColumnState ?? {};
      const formattedDate = actualDate ? format(actualDate, "do MMM yyyy") : "";

      let message = "";
      if (isReplacementLesson) {
        if (actualDate) {
          message = `For replacement lessons on ${formattedDate}`;
        } else if (replacementColumnIndex !== undefined) {
          // Assuming replacementColumnIndex can be 0, which is falsy
          message = `For replacement week ${replacementColumnIndex + 1}`;
        }
      } else if (isReplacementLesson === false && actualDate) {
        message = `For lessons on ${formattedDate}`;
      }

      return message;
    };

    const title = getTitle();

    return (
      <Actionsheet
        animationType="fade"
        hideDragIndicator
        isOpen={columnOptionsIsOpen}
        justifyContent="center"
        onClose={() => {
          columnOptionsOnClose();
          setTimeout(() => {
            setSelectedColumnState(undefined);
          }, 500);
        }}
        size="lg"
      >
        <Actionsheet.Content
          alignItems="center"
          closeButtonProps={{ right: "5", top: "5" }}
          justifyContent="center"
          mx="auto"
          roundedBottom={12}
          roundedTop={12}
          showCloseButton
          w="40%"
        >
          <Text alignSelf="center" fontSize="2xl" fontWeight="bold" py="4">
            Options
          </Text>
          {title ? (
            <Text
              alignSelf="center"
              fontFamily="Poppins-Regular"
              fontSize="md"
              mx="8"
              pb="2"
              textAlign="center"
            >
              {title}
            </Text>
          ) : null}
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={<RegisterIcon color="primary.500" size="6" />}
            onPress={() => {
              columnOptionsOnClose();
              setRegisterModalIsOpen(true);
            }}
          >
            Update register for week
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={<TimetableIcon color="primary.600" size="6" />}
            onPress={() => {
              setDatePickerState({
                date: selectedColumnState?.actualDate ?? new Date(),
                isOpen: true,
                replacementLessonColumnIndex: selectedColumnState?.replacementColumnIndex,
                scheduledDate: selectedColumnState?.scheduledDate,
              });
              columnOptionsOnClose();
              setTimeout(() => setSelectedColumnState(undefined), 500);
            }}
          >
            {!selectedColumnState?.isReplacementLesson
              ? "Reschedule lessons for week"
              : selectedColumnState?.actualDate
                ? "Reschedule replacement lessons for week"
                : "Assign date to replacement lessons for week"}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="6">
                {removeLessonColumnInFlight ? (
                  <Spinner color="surface.800" size="sm" />
                ) : (
                  <TrashIcon color="surface.800" size="6" />
                )}
              </Center>
            }
            onPress={() =>
              removeLessonColumn({
                replacementLessonColumnIndex: selectedColumnState?.replacementColumnIndex,
                scheduledDate: selectedColumnState?.actualDate
                  ? format(selectedColumnState?.actualDate, "yyyy-MM-dd")
                  : undefined,
              })
            }
          >
            {`Remove ${
              selectedColumnState?.isReplacementLesson ? "replacement " : ""
            }lesson column`}
          </Actionsheet.Item>
        </Actionsheet.Content>
      </Actionsheet>
    );
  }, [
    columnOptionsIsOpen,
    columnOptionsOnClose,
    removeLessonColumn,
    removeLessonColumnInFlight,
    selectedColumnState,
  ]);

  const renderModals = useMemo(() => {
    return (
      <>
        <ManageRegisterLessonModal
          hideModal={() => setRegisterModalIsOpen(false)}
          isUpdatingForWeek
          lessonInfo={{
            scheduledTimestamp: selectedColumnState?.actualDate
              ? format(selectedColumnState?.actualDate, "do MMM yyyy")
              : undefined,
            status: "PRESENT",
          }}
          showModal={registerModalIsOpen}
          updateRegistersForWeek={updateRegistersForWeekHandler}
          userIsTeacher={userIsTeacher}
        />
        <DatePickerModal
          accentColor={customColors.primary[800]}
          animationType="fade"
          date={datePickerState.date ?? new Date()}
          label={"Rearrange Lessons"}
          locale="en"
          mode="single"
          onConfirm={(params) => {
            if (params.date && datePickerState.scheduledDate !== undefined) {
              rescheduleLessonsForWeek({
                rearrangedDate: params.date,
                replacementLessonColumnIndex: datePickerState.replacementLessonColumnIndex,
                scheduledDate: datePickerState.scheduledDate,
                schoolId: state.values.schoolId,
              });
            }
            setDatePickerState({
              date: undefined,
              isOpen: false,
              replacementLessonColumnIndex: undefined,
              scheduledDate: undefined,
            });
          }}
          onDismiss={() =>
            setDatePickerState({
              date: undefined,
              isOpen: false,
              replacementLessonColumnIndex: undefined,
              scheduledDate: undefined,
            })
          }
          saveLabel="Save"
          startWeekOnMonday
          textStyle={{
            fontFamily: "Poppins-SemiBold",
          }}
          uppercase={false}
          visible={datePickerState.isOpen}
        />
        <DatePickerModal
          accentColor={customColors.primary[800]}
          animationType="fade"
          date={datePickerState.date ?? new Date()}
          label={"Schedule Replacement Lessons"}
          locale="en"
          mode="single"
          onConfirm={(params) => {
            if (params.date && addReplacementColumnDatePickerState.date !== undefined) {
              addReplacementLessonColumnToRegister({
                scheduledDate: params.date,
              });
            }
            setAddReplacementColumnDatePickerState({
              date: undefined,
              isOpen: false,
            });
          }}
          onDismiss={() =>
            setAddReplacementColumnDatePickerState({
              date: undefined,
              isOpen: false,
            })
          }
          saveLabel="Add"
          startWeekOnMonday
          textStyle={{
            fontFamily: "Poppins-SemiBold",
          }}
          uppercase={false}
          visible={addReplacementColumnDatePickerState.isOpen}
        />
      </>
    );
  }, [
    registerModalIsOpen,
    userIsTeacher,
    updateRegistersForWeekHandler,
    selectedColumnState?.actualDate,
    datePickerState.isOpen,
    datePickerState.date,
    datePickerState.scheduledDate,
    datePickerState.replacementLessonColumnIndex,
    addReplacementColumnDatePickerState.isOpen,
    addReplacementColumnDatePickerState.date,
    rescheduleLessonsForWeek,
    state.values.schoolId,
    addReplacementLessonColumnToRegister,
  ]);

  useEffect(() => {
    if (dataProvider.getSize() > 0) {
      recyclerListViewRef?.current?.scrollToIndex(0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.dayIndex]);

  useEffect(() => {
    dispatchState({ input: "contentIsRendered", value: true });
    // unsubscribe from refetch on unmount
    return () => state.values.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesToRender]);

  if (!state.values.contentIsRendered) {
    return <LoadingBlobs>Loading Registers...</LoadingBlobs>;
  }

  return (
    <>
      <VStack flex={1} flexGrow={1}>
        {renderTableHeader}
        <Box flex={1} minHeight={1} minWidth={1}>
          {dataProvider.getSize() > 0 ? (
            <RecyclerListView
              ref={recyclerListViewRef}
              canChangeSize
              dataProvider={dataProvider}
              layoutProvider={layoutProvider}
              onEndReached={onEndReached}
              renderFooter={renderFooter}
              rowRenderer={rowRenderer}
              scrollViewProps={{
                contentContainerStyle: {
                  borderBottomWidth:
                    (lessonBlocksData.lessonBlocks?.edges.length ?? 0 > 0) && !isLoadingNext
                      ? TABLE_BORDER_WIDTH
                      : 0,
                  borderColor:
                    // @ts-expect-error can't index with variable
                    colors?.[TABLE_BORDER_COLOR.split(".")[0]][TABLE_BORDER_COLOR.split(".")[1]],
                  marginBottom: 40,
                  overflow: "hidden",
                },
                showsVerticalScrollIndicator: false,
              }}
              style={{
                flex: 1,
                minHeight: 1,
              }}
              suppressBoundedSizeException
              useWindowScroll
            />
          ) : (
            renderListEmptyBanner
          )}
        </Box>
        {renderRefetchIndicator}
      </VStack>
      {renderColumnActionsheet}
      {renderModals}
    </>
  );
};

const TeacherRegisters = (props: TeacherRegistersProps): ReactElement => {
  const {
    allSchools,
    dispatchState,
    lessonBlocksConnectionId,
    loadLessonBlocksQuery,
    loadLessonBlocksQueryReference,
    navigation,
    profileFirstName,
    profileId,
    profileLastName,
    registerProgressCounter,
    registerUpdates,
    removeLessonBlocks,
    removeLessonBlocksInFlight,
    showUpdateBlockDetailsModalHandler,
    startingYears,
    state,
    teacherSchools,
    updateLessonBlockDetails,
    updateScheduledLessonBlockTime,
    updateScheduledLessonBlockTimeInFlight,
    userIsTeacher,
  } = props;

  const [dataProvider, setDataProvider] = useState(
    new DataProvider((r1, r2) => {
      return r1 !== r2;
    })
  );
  const [copyDataToClipboard, setCopyDataToClipboard] = useState<() => void | undefined>();

  const { width: windowWidth } = useWindowDimensions();

  const toast = useToast();

  const sendRegisterChangeNotificationModalKey = useRef(0);

  const [sendRegisterChangeNotificationInProgress, setSendRegisterChangeNotificationInProgress] =
    useState(false);

  const inputChangeHandler = useCallback(
    (_: unknown, inputValue?: string) => {
      dispatchState({
        input: "searchTerm",
        value: inputValue?.trim(),
      });
      navigation.setParams({
        searchTerm: inputValue?.trim(),
      });
    },
    [dispatchState, navigation]
  );

  const inputClearHandler = useCallback(() => {
    if (state.values.searchTerm !== "") {
      dispatchState({
        input: "searchTerm",
        value: "",
      });
    }
  }, [dispatchState, state.values.searchTerm]);

  const dateArrowHandler = useCallback(
    (isRightArrow: boolean) => {
      setSelectedBlocks([]);
      lessonBlockRefs.current.forEach((ref) => {
        ref.setIsChecked(false);
      });
      lessonBlockRefs.current = new Map();
      if (state.values.teachingDays) {
        const teachingDayIndex = state.values.teachingDays?.findIndex(
          (item) => item == state.values.dayIndex
        );
        if (teachingDayIndex >= 0) {
          if (isRightArrow) {
            if (teachingDayIndex !== state.values.teachingDays.length - 1) {
              dispatchState({
                input: "dayIndex",
                value: state.values.teachingDays[teachingDayIndex + 1] ?? state.values.dayIndex + 1,
              });
            } else {
              dispatchState({
                input: "dayIndex",
                value: state.values.teachingDays[0] ?? 0,
              });
            }
          } else {
            if (teachingDayIndex != 0) {
              dispatchState({
                input: "dayIndex",
                value: state.values.teachingDays[teachingDayIndex - 1] ?? state.values.dayIndex - 1,
              });
            } else {
              dispatchState({
                input: "dayIndex",
                value: state.values.teachingDays.slice(-1)?.[0] ?? 4,
              });
            }
          }
        }
      } else {
        if (isRightArrow) {
          if (state.values.dayIndex != 4) {
            dispatchState({
              input: "dayIndex",
              value: state.values.dayIndex + 1,
            });
          } else {
            dispatchState({
              input: "dayIndex",
              value: 0,
            });
          }
        } else {
          if (state.values.dayIndex != 0) {
            dispatchState({
              input: "dayIndex",
              value: state.values.dayIndex - 1,
            });
          } else {
            dispatchState({
              input: "dayIndex",
              value: 4,
            });
          }
        }
      }
    },
    [dispatchState, state.values.dayIndex, state.values.teachingDays]
  );

  const refreshHandler = useCallback(() => {
    const refreshData = () => {
      dispatchState({ input: "contentIsRendered", value: false });
      setSelectedBlocks([]);
      lessonBlockRefs.current.forEach((ref) => {
        ref.setIsChecked(false);
      });
      lessonBlockRefs.current = new Map();
      loadLessonBlocksQuery(
        {
          block: state.values.block,
          dayIndex: state.values.dayIndex,
          first: LOAD_X_LESSON_BLOCKS,
          profileId: profileId,
          schoolId: state.values.schoolId,
          searchTerm: state.values.searchTerm.trim(),
          skipLessonBlocks: false,
          skipLessonStages: true,
          skipRegisterProgress: false,
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "network-only" }
      );
      dispatchState({ input: "contentIsRendered", value: true });
    };
    if (registerUpdates.current.length > 0) {
      dispatchState({
        input: "leaveAlertAction",
        value: refreshData,
      });
    } else {
      refreshData();
    }
  }, [
    registerUpdates,
    dispatchState,
    loadLessonBlocksQuery,
    state.values.searchTerm,
    state.values.startingYear,
    state.values.block,
    state.values.schoolId,
    state.values.dayIndex,
    profileId,
  ]);

  const renderRegisterProgress = useMemo(() => {
    if (
      registerProgressCounter.current.total !== undefined &&
      registerProgressCounter.current.marked !== undefined
    ) {
      const percentageProgress =
        registerProgressCounter.current.total > 0
          ? 100 * (registerProgressCounter.current.marked / registerProgressCounter.current.total)
          : 0;
      let trackColor = "emerald.400";
      if (percentageProgress < 50) {
        trackColor = "red.300";
      } else if (percentageProgress < 90) {
        trackColor = "warning.300";
      } else if (percentageProgress < 100) {
        trackColor = "primary.300";
      }

      return (
        <Box>
          <Progress _filledTrack={{ bg: trackColor }} height="8" value={percentageProgress} />
          <Box alignItems="center" alignSelf="center" position="absolute" top="1.5">
            <Text>
              {percentageProgress.toFixed(1)}% ({registerProgressCounter.current.marked}/
              {registerProgressCounter.current.total}) up-to-date
            </Text>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box>
          <Progress height="8" value={0} />
        </Box>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerProgressCounter.current.total, registerProgressCounter.current.marked]);

  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const {
    isOpen: lessonBlockOptionsActionsheetIsOpen,
    onClose: lessonBlockOptionsActionsheetOnClose,
    onOpen: lessonBlockOptionsActionsheetOnOpen,
  } = useDisclose();

  const lessonBlockRefs = useRef(new Map<string, LessonBlockTableRowRef>());

  const renderDayPicker = useMemo(() => {
    return (state.values.teachingDays?.length ?? 0) > 0 ? (
      <Center mx="-6" px="6">
        <Button.Group>
          {(state.values.teachingDays?.length ?? 0) > 1 ? (
            <Button
              _hover={{ bg: "transparent", opacity: 0.8 }}
              _pressed={{ bg: "transparent", opacity: 0.7 }}
              leftIcon={<ChevronLeftIcon size={Platform.OS === "web" ? "6" : "5"} />}
              onPress={() => dateArrowHandler(false)}
              px="2"
              py="1"
              variant="ghost"
            />
          ) : (
            <></>
          )}
          <Center px="2" py="1" width="75px">
            <Tooltip
              isDisabled={!state.values.teachingDays}
              label={teachingDaysToString(state.values.teachingDays)}
              placement="bottom"
              pointerEvents="none"
              textAlign="center"
            >
              <Text color="primary.600" fontSize={Platform.OS === "web" ? "xl" : "lg"}>
                {getLessonDayString(state.values.dayIndex, {
                  sliceToLength: 3,
                })}
              </Text>
            </Tooltip>
          </Center>
          {(state.values.teachingDays?.length ?? 0) > 1 ? (
            <Button
              _hover={{ bg: "transparent", opacity: 0.8 }}
              _pressed={{ bg: "transparent", opacity: 0.7 }}
              leftIcon={<ChevronRightIcon size={Platform.OS === "web" ? "6" : "5"} />}
              onPress={() => dateArrowHandler(true)}
              px="2"
              py="1"
              variant="ghost"
            />
          ) : (
            <></>
          )}
        </Button.Group>
      </Center>
    ) : null;
  }, [state.values.teachingDays, state.values.dayIndex, dateArrowHandler]);

  const renderHeader = useMemo(() => {
    const sortedTeacherSchools = teacherSchools?.toSorted(
      (a, b) => a?.name.localeCompare(b?.name ?? "") ?? 0
    );

    return (
      <VStack mb="4" mx="-6" px="6" space="4">
        <HStack justifyContent="space-between">
          <HStack space="4">
            <Select
              borderRadius="2xl"
              fontSize="md"
              onValueChange={(itemValue) => {
                const value = parseInt(itemValue);

                if (value > state.values.startingYear) {
                  dispatchState({
                    input: "block",
                    value: 1,
                  });
                  navigation.setParams({
                    block: 1,
                  });
                }

                dispatchState({
                  input: "startingYear",
                  value: value,
                });
                navigation.setParams({
                  startingYear: value,
                });
              }}
              placeholder="Select year"
              selectedValue={String(state.values.startingYear)}
              width="40"
            >
              {startingYears.map((item) => {
                return (
                  <Select.Item
                    key={item.value}
                    actionSheetLabel={item.label}
                    value={String(item.value)}
                  />
                );
              })}
            </Select>
            <Select
              borderRadius="2xl"
              fontSize="md"
              onValueChange={(itemValue) => {
                dispatchState({
                  input: "block",
                  value: parseInt(itemValue),
                });
                navigation.setParams({
                  block: parseInt(itemValue),
                });
              }}
              placeholder="Select block"
              selectedValue={String(state.values.block)}
              width="40"
            >
              {BLOCKS.map((item) => {
                return (
                  <Select.Item key={item.value} actionSheetLabel={item.label} value={item.value} />
                );
              })}
            </Select>
            <Select
              borderRadius="2xl"
              color={state.values.schoolId ? "surface.900" : "muted.400"}
              fontSize="md"
              onValueChange={(itemValue) => {
                const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                dispatchState({
                  input: "schoolId",
                  value: parsedValue,
                });
                navigation.setParams({
                  schoolId: parsedValue,
                });
              }}
              placeholder="Select school"
              selectedValue={state.values.schoolId ?? "ANY"}
              width="56"
            >
              <Select.Item key={"ANY"} actionSheetLabel={"Any school"} value={"ANY"} />
              {sortedTeacherSchools?.map((item) => {
                if (!item?.id) {
                  return null;
                }

                return (
                  <Select.Item
                    key={item.id}
                    actionSheetLabel={titleCaseConverter(item.name) ?? ""}
                    value={item.id}
                  />
                );
              }) ??
                allSchools.map((item) => {
                  if (!item?.node?.id) {
                    return null;
                  }

                  const isArchived = item.node.archived;

                  return (
                    <Select.Item
                      key={item.node.id}
                      actionSheetLabel={`${titleCaseConverter(item.node.name)} ${
                        isArchived ? "(Archived)" : ""
                      }`}
                      value={item.node.id}
                    />
                  );
                })}
            </Select>
            {userIsTeacher ? (
              <>
                {Platform.OS === "web" && windowWidth > 1000 ? (
                  <Box flex={1} maxWidth="300">
                    <SearchBar
                      inputClearHandler={inputClearHandler}
                      inputOnChangeHandler={inputChangeHandler}
                      inputSearchHandler={inputChangeHandler}
                      placeholderText={"Search by pupil name"}
                      searchText={state.values.searchTerm}
                      showSearchIcon
                    />
                  </Box>
                ) : null}
                <Button
                  bg="surface.400"
                  colorScheme="surface"
                  height="10"
                  leftIcon={<RestartIcon size="5" />}
                  onPress={refreshHandler}
                  width="10"
                />
              </>
            ) : null}
          </HStack>
          {!userIsTeacher ? (
            <HStack space="4">
              <Button
                _hover={{ bg: "primary.500" }}
                _pressed={{ bg: "primary.600" }}
                _text={{ fontSize: "17" }}
                bg="primary.400"
                leftIcon={<AddCircleIcon size="md" />}
                onPress={() => {
                  dispatchState({
                    input: "selectedSchoolIdOnModal",
                    value:
                      state.values.schoolId ?? teacherSchools?.[0]?.id ?? allSchools[0]?.node?.id,
                  });
                  dispatchState({
                    input: "selectSchoolModalIsOpen",
                    value: true,
                  });
                }}
                px="4"
                shadow={1}
              >
                Add Lesson Block
              </Button>
              <Button
                _hover={{ bg: "primary.500" }}
                _pressed={{ bg: "primary.600" }}
                _text={{ fontSize: "17" }}
                bg="primary.400"
                leftIcon={<AddCircleIcon size="md" />}
                onPress={() => {
                  dispatchState({
                    input: "selectedSchoolIdOnModal",
                    value:
                      state.values.schoolId ?? teacherSchools?.[0]?.id ?? allSchools[0]?.node?.id,
                  });
                  dispatchState({
                    input: "selectSchoolModalIsOpen",
                    value: true,
                  });
                  dispatchState({
                    input: "isAddLessonBreakModal",
                    value: true,
                  });
                }}
                px="4"
                shadow={1}
              >
                Add Break
              </Button>
            </HStack>
          ) : null}
        </HStack>
        {!userIsTeacher ? (
          <HStack justifyContent="space-between">
            <HStack space="4" width="400">
              <Box flex={1}>
                <SearchBar
                  inputClearHandler={inputClearHandler}
                  inputOnChangeHandler={inputChangeHandler}
                  inputSearchHandler={inputChangeHandler}
                  placeholderText={"Search by pupil name"}
                  searchText={state.values.searchTerm}
                  showSearchIcon
                />
              </Box>
              <Button
                bg="surface.400"
                colorScheme="surface"
                height="10"
                leftIcon={<RestartIcon size="5" />}
                onPress={refreshHandler}
                width="10"
              />
            </HStack>
            <Button
              _hover={{ bg: "primary.500" }}
              _pressed={{ bg: "primary.600" }}
              _text={{ fontSize: "17" }}
              bg="primary.400"
              leftIcon={<CopyIcon size="md" />}
              onPress={copyDataToClipboard}
              px="4"
              shadow={1}
            >
              Copy Table to Clipboard
            </Button>
          </HStack>
        ) : null}
        {Platform.OS === "web" ? renderRegisterProgress : null}
        <HStack justifyContent="space-between">
          <PresenceTransition
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                duration: 250,
              },
            }}
            initial={{
              opacity: 0,
              scale: 0,
            }}
            visible={selectedBlocks.length > 0}
          >
            <Button
              _text={{ fontSize: "17" }}
              colorScheme="surface"
              leftIcon={
                <Center size="5">
                  <OptionsIcon color="surface.100" size="5" />
                </Center>
              }
              onPress={() => lessonBlockOptionsActionsheetOnOpen()}
              pr="4"
              shadow={1}
            >
              {`View Options (${selectedBlocks.length})`}
            </Button>
          </PresenceTransition>
          {renderDayPicker}
          <Button
            _hover={{ bg: "primary.500" }}
            _pressed={{ bg: "primary.600" }}
            _text={{ fontSize: "17" }}
            bg="primary.400"
            disabled={sendRegisterChangeNotificationInProgress}
            leftIcon={
              <Center mr="2px" size="5">
                {sendRegisterChangeNotificationInProgress ? (
                  <Spinner color="surface.100" />
                ) : (
                  <SendIcon color="surface.100" size="5" />
                )}
              </Center>
            }
            onPress={() => {
              dispatchState({
                input: "sendRegisterChangeNotificationModalIsOpen",
                value: true,
              });
            }}
            px="4"
            shadow={1}
          >
            {"Send Register Change SMS"}
          </Button>
        </HStack>
      </VStack>
    );
  }, [
    allSchools,
    copyDataToClipboard,
    dispatchState,
    inputChangeHandler,
    inputClearHandler,
    lessonBlockOptionsActionsheetOnOpen,
    navigation,
    refreshHandler,
    renderDayPicker,
    renderRegisterProgress,
    selectedBlocks.length,
    sendRegisterChangeNotificationInProgress,
    startingYears,
    state.values.block,
    state.values.schoolId,
    state.values.searchTerm,
    state.values.startingYear,
    teacherSchools,
    userIsTeacher,
    windowWidth,
  ]);

  const renderLessonBlockOptionsActionsheet = useMemo(() => {
    return (
      <Actionsheet
        animationType="fade"
        hideDragIndicator
        isOpen={lessonBlockOptionsActionsheetIsOpen}
        justifyContent="center"
        onClose={lessonBlockOptionsActionsheetOnClose}
        size="lg"
      >
        <Actionsheet.Content
          alignItems="center"
          closeButtonProps={{ right: "5", top: "5" }}
          justifyContent="center"
          mx="auto"
          roundedBottom={12}
          roundedTop={12}
          showCloseButton
          w="40%"
        >
          <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
            {`Lesson Block Actions (${selectedBlocks.length})`}
          </Text>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <AccountsIcon color="primary.500" size="5" />
              </Center>
            }
            onPress={() => {
              lessonBlockOptionsActionsheetOnClose();
              showUpdateBlockDetailsModalHandler({
                lessonBlockIds: selectedBlocks,
                onUpdate: () => {
                  refreshHandler();
                },
                selectedProfileInfo: {
                  fullName: profileFirstName
                    ? getFullName(profileFirstName, profileLastName)
                    : undefined,
                  id: profileId,
                },
                type: "TEACHER",
              });
            }}
          >
            {`Change block${selectedBlocks.length > 1 ? "s" : ""} to different teacher`}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <CalendarIcon color="primary.500" size="6" />
              </Center>
            }
            onPress={() => {
              lessonBlockOptionsActionsheetOnClose();
              dispatchState({
                input: "updateLessonBlockTimeModalIsOpen",
                value: true,
              });
            }}
          >
            {`Change block${selectedBlocks.length > 1 ? "s" : ""} to different day`}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <UserClockIcon color="primary.600" size="5" />
              </Center>
            }
            onPress={() => {
              lessonBlockOptionsActionsheetOnClose();
              showUpdateBlockDetailsModalHandler({
                lessonBlockIds: selectedBlocks,
                onUpdate: () => {
                  refreshHandler();
                },
                selectedProfileInfo: {
                  fullName: profileFirstName
                    ? getFullName(profileFirstName, profileLastName)
                    : undefined,
                  id: profileId,
                },
                type: "TEACHER_AND_DAY",
              });
            }}
          >
            {`Change block${selectedBlocks.length > 1 ? "s" : ""} to different teacher and day`}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <UserSlashIcon color="error.500" size="5" />
              </Center>
            }
            onPress={() => {
              lessonBlockOptionsActionsheetOnClose();
              updateLessonBlockDetails(
                {
                  lessonBlockIds: selectedBlocks,
                  pupilId: null,
                },
                undefined,
                () => {
                  setSelectedBlocks([]);
                  lessonBlockRefs.current.forEach((ref) => {
                    ref.setIsChecked(false);
                  });
                }
              );
            }}
          >
            {`Remove assigned pupil from block${selectedBlocks.length > 1 ? "s" : ""}`}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <EditIcon color="secondary.600" size="6" />
              </Center>
            }
            onPress={() => {
              lessonBlockOptionsActionsheetOnClose();
              showUpdateBlockDetailsModalHandler({
                lessonBlockIds: selectedBlocks,
                onUpdate: () => {
                  setSelectedBlocks([]);
                  lessonBlockRefs.current.forEach((ref) => {
                    ref.setIsChecked(false);
                  });
                },
                type: "NOTES",
              });
            }}
          >
            {`Add/update note for lesson block${selectedBlocks.length > 1 ? "s" : ""}`}
          </Actionsheet.Item>
          {removeLessonBlocks !== undefined ? (
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  {removeLessonBlocksInFlight ? (
                    <Spinner color="surface.800" size="sm" />
                  ) : (
                    <TrashIcon color="surface.800" size="5" />
                  )}
                </Center>
              }
              onPress={() => {
                removeLessonBlocks(selectedBlocks, lessonBlocksConnectionId, () => {
                  lessonBlockOptionsActionsheetOnClose();
                  setSelectedBlocks([]);
                  lessonBlockRefs.current.forEach((ref) => {
                    ref.setIsChecked(false);
                  });
                });
              }}
            >
              {`Delete lesson block${selectedBlocks.length > 1 ? "s" : ""}`}
            </Actionsheet.Item>
          ) : null}
        </Actionsheet.Content>
      </Actionsheet>
    );
  }, [
    dispatchState,
    lessonBlockOptionsActionsheetIsOpen,
    lessonBlockOptionsActionsheetOnClose,
    lessonBlocksConnectionId,
    profileFirstName,
    profileId,
    profileLastName,
    refreshHandler,
    removeLessonBlocks,
    removeLessonBlocksInFlight,
    selectedBlocks,
    showUpdateBlockDetailsModalHandler,
    updateLessonBlockDetails,
  ]);

  const renderUpdateScheduledLessonBlockTimeDayPicker = useMemo(() => {
    if (!updateScheduledLessonBlockTime) {
      return null;
    }

    return (
      <DayPickerModal
        hideModal={() =>
          dispatchState({
            input: "updateLessonBlockTimeModalIsOpen",
            value: false,
          })
        }
        initialLessonDay={state.values.dayIndex}
        lessonBlockIds={selectedBlocks}
        onUpdate={() => {
          setSelectedBlocks([]);
          lessonBlockRefs.current.forEach((ref) => {
            ref.setIsChecked(false);
          });
        }}
        showModal={Boolean(state.values.updateLessonBlockTimeModalIsOpen)}
        updateScheduledLessonBlockTime={updateScheduledLessonBlockTime}
        updateScheduledLessonBlockTimeInFlight={updateScheduledLessonBlockTimeInFlight ?? false}
      />
    );
  }, [
    dispatchState,
    selectedBlocks,
    state.values.dayIndex,
    state.values.updateLessonBlockTimeModalIsOpen,
    updateScheduledLessonBlockTime,
    updateScheduledLessonBlockTimeInFlight,
  ]);

  const renderSendRegisterChangeNotificationModal = useMemo(() => {
    return (
      <SendRegisterChangeNotificationModal
        key={sendRegisterChangeNotificationModalKey.current}
        hideModal={() => {
          dispatchState({
            input: "sendRegisterChangeNotificationModalIsOpen",
            value: false,
          });
          // reset state on close
          setTimeout(() => {
            sendRegisterChangeNotificationModalKey.current = Math.random();
          }, 500);
        }}
        onSendFinish={(success) => {
          setSendRegisterChangeNotificationInProgress(false);
          if (success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="success" title={"Message sent"} toast={toast} />
              ),
            });
          }
        }}
        onSendStart={() => setSendRegisterChangeNotificationInProgress(true)}
        showModal={Boolean(state.values.sendRegisterChangeNotificationModalIsOpen)}
        teacherId={profileId}
        teacherName={profileFirstName}
      />
    );
  }, [
    dispatchState,
    profileFirstName,
    profileId,
    state.values.sendRegisterChangeNotificationModalIsOpen,
    toast,
  ]);

  return (
    <Box flex={1}>
      {renderHeader}
      {loadLessonBlocksQueryReference != null ? (
        <Suspense fallback={<LoadingBlobs>Loading Registers...</LoadingBlobs>}>
          <TeacherRegistersContent
            {...props}
            dataProvider={dataProvider}
            lessonBlockRefs={lessonBlockRefs}
            loadLessonBlocksQueryReference={loadLessonBlocksQueryReference}
            refreshHandler={refreshHandler}
            selectedBlocks={selectedBlocks}
            setCopyDataToClipboard={setCopyDataToClipboard}
            setDataProvider={setDataProvider}
            setSelectedBlocks={setSelectedBlocks}
          />
        </Suspense>
      ) : (
        <LoadingBlobs>Loading Registers...</LoadingBlobs>
      )}
      {renderLessonBlockOptionsActionsheet}
      {renderUpdateScheduledLessonBlockTimeDayPicker}
      {renderSendRegisterChangeNotificationModal}
    </Box>
  );
};

export default React.memo(TeacherRegisters);
